// @flow
import bowser from 'bowser';
import type { $Request } from 'express';

type UtilFunction = (userAgentString?: string) => ?boolean;
type BowserFunction = string => boolean;

export const MOBILE_EMBEDDED_HEADER = 'HTTP_X_MOBILE_EMBEDDED';
export const MOBILE_EMBEDDED_COOKIE = 'mobile-embedded';
export const EMBEDDED_WEB_CLASS_NAME = 'js-mobile-embedded';

// Takes in a BowserFunction and makes it safe to call with or without a string arg or the window present
export const safeWindowFunc = (bowserFuncToWrap: BowserFunction): UtilFunction => (
  userAgentString?: string
): ?boolean => {
  if (userAgentString) {
    return bowserFuncToWrap(userAgentString);
  } else if (
    window &&
    window.navigator &&
    typeof window.navigator.userAgent === 'string' &&
    window.navigator.userAgent.length > 0
  ) {
    return bowserFuncToWrap(window.navigator.userAgent);
  }
  throw new Error(
    'userAgent utils must be passed a User-Agent string or window.navigator.userAgent must be defined'
  );
};

// bowser-based functions
export const isAndroid: UtilFunction = safeWindowFunc(
  (userAgentString: string) => bowser.getParser(userAgentString).parsedResult.os.name === 'Android'
);

export const isWindowsPhone: UtilFunction = safeWindowFunc(
  (userAgentString: string) =>
    bowser.getParser(userAgentString).parsedResult.os.name === 'Windows Phone'
);

export const isKindle: UtilFunction = safeWindowFunc(
  (userAgentString: string) =>
    bowser.getParser(userAgentString).parsedResult.platform.vendor === 'Amazon'
);

export const isiOS: UtilFunction = safeWindowFunc(
  (userAgentString: string) => bowser.getParser(userAgentString).parsedResult.os.name === 'iOS'
);

export const isiOS9: UtilFunction = safeWindowFunc((userAgentString: string) => {
  const { parsedResult } = bowser.getParser(userAgentString);
  return (
    parsedResult.os.name === 'iOS' &&
    typeof parsedResult.os.version === 'string' &&
    parsedResult.os.version.startsWith('9.')
  );
});

export const isMobileBrowser: UtilFunction = safeWindowFunc(
  (userAgentString: string) =>
    bowser.getParser(userAgentString).parsedResult.platform.type === 'mobile'
);

export const isIE: UtilFunction = safeWindowFunc(
  (userAgentString: string) =>
    bowser.getParser(userAgentString).parsedResult.browser.name === 'Internet Explorer'
);

export const isIE11: UtilFunction = safeWindowFunc((userAgentString: string) => {
  const { parsedResult } = bowser.getParser(userAgentString);
  return (
    parsedResult.browser.name === 'Internet Explorer' &&
    typeof parsedResult.browser.version === 'string' &&
    parsedResult.browser.version.startsWith('11.')
  );
});

export const isEdge: UtilFunction = safeWindowFunc(
  (userAgentString: string) =>
    bowser.getParser(userAgentString).parsedResult.browser.name === 'Microsoft Edge'
);

export const isChrome: UtilFunction = safeWindowFunc(
  (userAgentString: string) =>
    bowser.getParser(userAgentString).parsedResult.browser.name === 'Chrome'
);

// misc functions
export const isHoverable = (): boolean =>
  window && window.matchMedia ? !window.matchMedia('(hover: none)').matches : false;

export const isMobileEmbedded = (): boolean =>
  document && document.body ? document.body.classList.contains(EMBEDDED_WEB_CLASS_NAME) : false;

// Only use server-side
export const isExpressRequestMobileEmbedded = (req: $Request): boolean =>
  !!req.header(MOBILE_EMBEDDED_HEADER) ||
  (req.get('cookie') && req.get('cookie').includes(MOBILE_EMBEDDED_COOKIE));



// WEBPACK FOOTER //
// ../utilities/userAgent.js